import { type ISurveyItem, type ISurveySection } from '../../../../models/SurveyTemplate'
import { type SectionItem, type ISection, type SectionBranch, type SectionResponseItem } from './types'

// Separator used to combine multiple choice options into a single string
export const MULTIPLE_CHOICE_SEPERATOR = '___'

// Separator used to concatenate instructorId and surveySectionId into a modded section ID
export const SECTION_ID_SEPERATOR = '|'

/**
 * Combines instructorId and surveySectionId into a single modded section ID string.
 * If instructorId is undefined, it defaults to an empty string.
 *
 * Example Output: "123|456" or "|456"
 */
const getModdedSectionId = (instructorId: ISurveySection['instructorId'] | undefined, surveySectionId: ISection['surveySectionId']): string => (instructorId ?? '') + SECTION_ID_SEPERATOR + surveySectionId

/**
 * Parses a modded section ID string back into its component parts: instructorId and surveySectionId.
 * If the instructorId portion is the string 'undefined', it is returned as actual `undefined`.
 *
 * Example Input: "123|456"
 * Output: { instructorId: "123", surveySectionId: "456" }
 */
const parseModdedSectionId = (moddedSectionId: string): { instructorId: ISurveySection['instructorId'] | undefined, surveySectionId: ISection['surveySectionId'] } => {
  const [instructorID, surveySectionId] = moddedSectionId.split(SECTION_ID_SEPERATOR)

  return { instructorId: instructorID === 'undefined' ? undefined : instructorID, surveySectionId }
}

/**
 * Takes an array of section items where the `surveySectionId` is a modded string,
 * and replaces the modded ID with just the `surveySectionId` part by parsing it.
 *
 * Useful for reverting transformed data back to its original form.
 */
export const bulkParseModdedSectionIdForSectionItems = (sectionItems: SectionResponseItem[]): SectionResponseItem[] => {
  return sectionItems.map(
    item => ({ ...item, surveySectionId: parseModdedSectionId(item.surveySectionId).surveySectionId })
  )
}

/**
 * Transforms an array of ISurveySection objects into a structured hierarchy of ISection objects.
 * - Creates unique modded section IDs by combining instructorId and surveySectionId.
 * - Resolves parent-child relationships between sections using these modded IDs.
 * - Handles branching logic by transforming survey items accordingly.
 *
 * Returns a tree-like structure of ISection objects.
 */
export const transformSections = (sections: ISurveySection[]): ISection[] => {
  const sectionMap: Record<string, ISection> = {}
  const transformedSections: ISection[] = []

  // First pass: map each section to a new ISection structure with modded IDs
  sections.forEach(section => {
    const {
      surveySectionId,
      parentSurveySectionId,
      instructorId,
      title,
      descr,
      instructorName,
      branchingFilters
    } = section

    const moddedSectionId = getModdedSectionId(instructorId, surveySectionId)

    // Helper to get the modded ID of a given sectionId, accounting for its instructorId
    const getModdedId = (sectionId: ISection['surveySectionId']): string => {
      const section = sections.find(s => s.surveySectionId === sectionId)
      return getModdedSectionId(section?.instructorId, sectionId)
    }

    // If branching filters exist, capture the origin information
    const origin = branchingFilters ? { itemId: branchingFilters.itemId, operation: branchingFilters.operation, type: branchingFilters.itemType } : undefined

    // Apply branching logic to survey items
    const surveyItems = getItemsWithBranches(
      section.surveyItems,
      getModdedId
    )

    // Generate modded parent section ID if a parent exists
    const moddedparentSectionId = parentSurveySectionId && getModdedId(parentSurveySectionId)

    // Populate sectionMap with the transformed section
    sectionMap[moddedSectionId] = {
      surveySectionId: moddedSectionId,
      parentSurveySectionId: moddedparentSectionId,
      instructorId,
      title,
      descr,
      instructorName,
      surveyItems,
      origin,
      children: []
    }
  })

  // Second pass: assign child sections to their respective parents
  sections.forEach(section => {
    const moddedSectionId = getModdedSectionId(section.instructorId, section.surveySectionId)
    const mappedSection = sectionMap[moddedSectionId]

    if (!mappedSection.parentSurveySectionId) {
      // Top-level section (no parent)
      transformedSections.push(mappedSection)
    } else {
      // Attach as a child to its parent
      sectionMap[mappedSection.parentSurveySectionId].children.push(mappedSection)
    }
  })

  return transformedSections
}

/**
 * Validates the structure of each section and its child sections.
 * Returns a record where each key is a section's `surveySectionId`,
 * and the value is `true` if that section (and all its children) pass validation.
 */
export const validateDataFormatForSections = (sections: ISection[]): Record<string, boolean> => {
  const sectionValidity: Record<ISection['surveySectionId'], boolean> = {}
  sections.forEach(section => { sectionValidity[section.surveySectionId] = validateSectionChildren(section) })

  return sectionValidity
}

/**
 * Recursively validates that each child section is correctly linked to its parent.
 * A child section is valid if:
 * - It has a corresponding branch in one of the parent section’s survey items.
 * - All of its own children are also valid.
 *
 * Returns `true` if all children (recursively) are valid, otherwise `false`.
 */
const validateSectionChildren = (section: ISection): boolean => {
  for (const child of section.children) {
    const childSectionId = child.surveySectionId

    // Find the item that originated the branch to this child
    const originItemId = child.origin?.itemId
    const item = section.surveyItems.find(item => item.itemId === originItemId)

    // Check if the branch to this child section exists in the item’s branches
    const branch = item?.branches.find(branch => branch.sectionId === childSectionId)

    // If the current section has a parent, its child must have a valid branch
    if (section.parentSurveySectionId && !branch) return false

    // Recursively validate this child’s own children
    if (!validateSectionChildren(child)) return false
  }

  return true
}

/**
 * Transforms raw survey items into enriched section items, adding branching metadata.
 * - Each item is mapped to a new object with a default `value` and a `branches` array.
 * - If the item contains branching filters, they are converted to `SectionBranch` objects
 *   with a mapped (modded) section ID.
 *
 * Used to prep survey items for branching logic.
 */
export const getItemsWithBranches = (items: ISurveyItem[], getModdedSectionId: (sectionId: ISection['surveySectionId']) => string): SectionItem[] => {
  return items?.map((item) => {
    const value = '' // Default response value for the item

    const { itemId, itemType, prompt, object, isRequired, revision } = item

    // Convert branching filters into structured branches with mapped section IDs
    const branches = item?.branchingFilters?.length > 0
      ? item?.branchingFilters?.map((branch: any) => {
        const branchingValue = branch.key
        const sectionId = getModdedSectionId(branch.surveySectionId)

        return { branchingValue, sectionId }
      }) as SectionBranch[]
      : []

    return { itemId, itemType, prompt, object, isRequired, value, revision, branches }
  })
}

/**
 * Searches for a survey item with a specific `itemId` within a section and its descendants.
 * - Returns the first matching item found.
 * - If not found in the current section, recursively searches through all children.
 *
 * Useful for finding the origin of a branching condition in nested sections.
 */
export const findOrigin = (section: ISection, itemId: SectionItem['itemId']): SectionItem | undefined => {
  // Check if item exists in current section
  const item = section.surveyItems.find(item => item.itemId === itemId)

  if (item) return item

  // If no children, stop the search
  if (section.children.length === 0) return undefined

  // Recursively search in children
  for (const childSection of section.children) {
    const item = findOrigin(childSection, itemId)
    if (item) return item
  }

  return undefined
}
